// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Huntress — the three modes. A mode has to change how the game PLAYS, not just a number.
//
// The HOST's pick travels frozen inside rematch.ts's round start; the board, the huntresses and the
// opening are DERIVED from mode alone on every peer, so a mode can never desync. modeOf() guards an
// id off the wire with Object.hasOwn — a bare `MODES[id] ||` lets 'constructor' through as a Mode
// of undefined fields.
//
// THERE IS NO PIE/SWAP RULE. An earlier cut carried a `swap` flag and a whole T_SWAP ply, and it was
// unreachable: the normal-ply path cleared `swapOpen` on ply 0, before the only window the gate
// could fire in, and no UI ever offered it. It was removed rather than finished, because the thing
// it exists to correct is not there — measured first-move advantage is 45–53% across the three modes
// (tests/balance.test.ts), and sides alternate every round in both solo and multiplayer, which is
// the mechanism that actually keeps a match fair.

export interface Mode {
  id: string;
  name: string;
  blurb: string;
  w: number;
  h: number;
  /** Huntresses per side. */
  huntresses: number;
  /** Fallback opening if the seeded draw cannot satisfy its constraints. Side 0 first. */
  fallback: number[];
}

/** (x, y) → index, for authoring openings readably. */
const at = (w: number) => (x: number, y: number): number => y * w + x;

const a6 = at(6);
const a7 = at(7);
const a8 = at(8);

export const MODES: Record<string, Mode> = {
  thicket: {
    id: 'thicket',
    name: 'Thicket',
    blurb: '6×6, two huntresses each. Close quarters — every arrow is felt on the very next turn.',
    w: 6,
    h: 6,
    huntresses: 2,
    fallback: [a6(1, 5), a6(4, 5), a6(4, 0), a6(1, 0)],
  },
  moorland: {
    id: 'moorland',
    name: 'Moorland',
    blurb: '7×7, three huntresses each. Room to manoeuvre, so fencing off ground is the whole game.',
    w: 7,
    h: 7,
    huntresses: 3,
    fallback: [a7(0, 4), a7(3, 6), a7(6, 4), a7(6, 2), a7(3, 0), a7(0, 2)],
  },
  greatwood: {
    id: 'greatwood',
    name: 'Greatwood',
    blurb: '8×8, four huntresses each. The long hunt — you can lose a huntress to a corner and still win.',
    w: 8,
    h: 8,
    huntresses: 4,
    fallback: [a8(0, 5), a8(2, 7), a8(5, 7), a8(7, 5), a8(7, 2), a8(5, 0), a8(2, 0), a8(0, 2)],
  },
};

export const MODE_IDS: string[] = ['thicket', 'moorland', 'greatwood'];
export const DEFAULT_MODE = 'thicket';

export function modeOf(id: string | null | undefined): Mode {
  if (typeof id === 'string' && Object.hasOwn(MODES, id)) return MODES[id];
  return MODES[DEFAULT_MODE];
}
